import { City } from '@/data/cities';
import { CitySpecialty } from '@/data/citySpecialties';
import { CityLanguage } from '@/context/languages/languageData';
import { 
  getSpecialtyNameForLanguage, 
  getSpecialtyDescriptionForLanguage,
  getSpecialtyLongDescriptionForLanguage 
} from '@/utils/cityLanguage/getSpecialtySlugForLanguage';
import { formatCityFullName } from '@/utils/seo/contentGenerator';

export interface SpecialtyContent {
  title: string;
  metaDescription: string;
  h1: string;
  heroDescription: string;
  whyChooseTitle: string;
  whyChoosePoints: string[];
  howItWorksTitle: string;
  howItWorksSteps: { title: string; description: string }[];
  faqTitle: string;
  faqs: { question: string; answer: string }[];
}

export const generateSpecialtyContent = (
  city: City,
  specialty: CitySpecialty,
  language: CityLanguage = 'en'
): SpecialtyContent => {
  const specialtyName = getSpecialtyNameForLanguage(specialty, language);
  const fullLocation = formatCityFullName(city);
  const cityName = city.name.replace(/\s+\d+$/, '').trim();
  const state = city.state_province;
  const specialtyDesc = getSpecialtyDescriptionForLanguage(specialty, language);
  const longDesc = getSpecialtyLongDescriptionForLanguage(specialty, language);

  if (language === 'es') {
    return {
      title: `${specialtyName} en ${fullLocation} - Encuentra tu Terapeuta Ideal`,
      metaDescription: `Encuentra los mejores ${specialtyName.toLowerCase()} en ${fullLocation}. ${specialtyDesc}. Terapia presencial y online disponible.`,
      h1: `${specialtyName} en ${fullLocation}`,
      heroDescription: `Conecta con ${specialtyName.toLowerCase()} certificados en ${fullLocation}. ${longDesc}`,
      whyChooseTitle: `¿Por qué elegir un ${specialtyName} en ${cityName}${state ? `, ${state}` : ''}?`,
      whyChoosePoints: [
        `Profesionales certificados y especializados en ${cityName}`,
        'Terapia presencial y online disponible',
        'Enfoque personalizado basado en evidencia',
        'Primera sesión de evaluación sin compromiso',
        'Flexibilidad de horarios adaptados a ti'
      ],
      howItWorksTitle: '¿Cómo funciona?',
      howItWorksSteps: [
        {
          title: 'Explora Perfiles',
          description: `Revisa los perfiles de ${specialtyName.toLowerCase()} en ${cityName} y encuentra el que mejor se adapte a tus necesidades.`
        },
        {
          title: 'Reserva tu Primera Sesión',
          description: 'Elige el horario que más te convenga para tu primera consulta, presencial u online.'
        },
        {
          title: 'Comienza tu Terapia',
          description: 'Inicia tu proceso terapéutico con profesionales certificados que te ayudarán a alcanzar tus objetivos.'
        }
      ],
      faqTitle: 'Preguntas Frecuentes',
      faqs: [
        {
          question: `¿Qué hace un ${specialtyName}?`,
          answer: `Un ${specialtyName} es un profesional especializado que ${specialtyDesc.toLowerCase()}. Utilizan enfoques terapéuticos basados en evidencia para ayudarte.`
        },
        {
          question: `¿Cuánto cuesta una sesión con un ${specialtyName} en ${cityName}?`,
          answer: `El costo de una sesión varía según el profesional y el tipo de terapia. En ${cityName}, los precios suelen oscilar entre 50-150€ por sesión. Muchos profesionales ofrecen la primera consulta a precio reducido.`
        },
        {
          question: '¿Puedo hacer terapia online?',
          answer: `Sí, muchos ${specialtyName.toLowerCase()} en ${cityName} ofrecen sesiones online mediante videoconferencia segura, permitiéndote recibir atención profesional desde cualquier lugar.`
        },
        {
          question: '¿Cuánto dura un proceso terapéutico?',
          answer: 'La duración varía según tus necesidades individuales. Algunos pacientes notan mejoras en pocas sesiones, mientras que otros procesos pueden requerir varios meses de tratamiento continuo.'
        },
        {
          question: '¿Cómo sé si necesito este tipo de terapia?',
          answer: `Si experimentas dificultades relacionadas con ${specialty.keywords[language]?.[0] || specialty.keywords.en[0]}, un ${specialtyName} puede ayudarte a desarrollar estrategias efectivas para superarlas.`
        }
      ]
    };
  }

  // English content
  return {
    title: `${specialtyName} in ${fullLocation} - Find Your Ideal Therapist`,
    metaDescription: `Find the best ${specialtyName.toLowerCase()} in ${fullLocation}. ${specialtyDesc}. In-person and online therapy available.`,
    h1: `${specialtyName} in ${fullLocation}`,
    heroDescription: `Connect with certified ${specialtyName.toLowerCase()} in ${fullLocation}. ${longDesc}`,
    whyChooseTitle: `Why Choose a ${specialtyName} in ${cityName}${state ? `, ${state}` : ''}?`,
    whyChoosePoints: [
      `Certified and specialized professionals in ${cityName}`,
      'In-person and online therapy available',
      'Evidence-based personalized approach',
      'First evaluation session with no commitment',
      'Flexible scheduling adapted to you'
    ],
    howItWorksTitle: 'How It Works',
    howItWorksSteps: [
      {
        title: 'Explore Profiles',
        description: `Browse ${specialtyName.toLowerCase()} profiles in ${cityName} and find the one that best fits your needs.`
      },
      {
        title: 'Book Your First Session',
        description: 'Choose the time that works best for your first consultation, in-person or online.'
      },
      {
        title: 'Start Your Therapy',
        description: 'Begin your therapeutic process with certified professionals who will help you achieve your goals.'
      }
    ],
    faqTitle: 'Frequently Asked Questions',
    faqs: [
      {
        question: `What does a ${specialtyName} do?`,
        answer: `A ${specialtyName} is a specialized professional who ${specialtyDesc.toLowerCase()}. They use evidence-based therapeutic approaches to help you.`
      },
      {
        question: `How much does a session with a ${specialtyName} in ${cityName} cost?`,
        answer: `Session costs vary by professional and therapy type. In ${cityName}, prices typically range from $60-180 per session. Many professionals offer a reduced rate for the first consultation.`
      },
      {
        question: 'Can I do online therapy?',
        answer: `Yes, many ${specialtyName.toLowerCase()} in ${cityName} offer online sessions through secure video conferencing, allowing you to receive professional care from anywhere.`
      },
      {
        question: 'How long does a therapeutic process last?',
        answer: 'Duration varies based on your individual needs. Some patients notice improvements in a few sessions, while other processes may require several months of continuous treatment.'
      },
      {
        question: 'How do I know if I need this type of therapy?',
        answer: `If you're experiencing difficulties related to ${specialty.keywords.en[0]}, a ${specialtyName} can help you develop effective strategies to overcome them.`
      }
    ]
  };
};
